'use client'

import Image from 'next/image'
import { useState } from 'react'
import styled from 'styled-components'

import { colors, spaces } from '@/config/global-parameters'

import reportAnalytics from '../analyticsEvents'
import LiveIndicator from './LiveIndicator'
import MobileContent, { MobileContainer } from './MobileContent'

interface IYoutubeLiveVideo {
  autoplay?: boolean
  expandedHeight?: number
  expandedWidth?: number
  mobileAutoplay?: boolean
  smallHeight?: number
  smallWidth?: number
  videoId: string
  headerHeight?: number
}

const caretUp = (
  <Image
    alt="caret_down"
    height="10"
    src="/images/caret_down.svg"
    width="12"
  />
)
const caretDown = (
  <Image
    alt="caret_up"
    height="10"
    src="/images/caret_up.svg"
    width="12"
  />
)
const shrinkArrow = (
  <Image
    alt="shrink_arrow"
    height={9}
    src="/images/shrink_arrow.svg"
    width={9}
  />
)
const growArrow = (
  <Image
    alt="grow_arrow"
    height={9}
    src="/images/grow_arrow.svg"
    width={9}
  />
)

const allowParams = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'

const YoutubeLiveVideo = ({
  autoplay = true,
  expandedHeight = 443,
  expandedWidth = 799,
  headerHeight = 44,
  smallHeight = 136,
  smallWidth = 246,
  videoId,
}: IYoutubeLiveVideo) => {
  const disableLive = Boolean(process?.env?.NEXT_PUBLIC_DISABLE_LIVE === 'true')
  const [isActive, setIsActive] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const sendSignalLeft = () => {
    setIsActive(true)
    setIsExpanded(!isExpanded)
    if (!isActive) reportAnalytics('live_video_event', 'expand')
  }

  const sendSignalRight = () => {
    setIsActive(!isActive)
    setIsExpanded(false)
    if (!isActive) reportAnalytics('live_video_event', 'dropdown')
  }

  const viewHeight = isExpanded ? expandedHeight : smallHeight
  const viewWidth = isExpanded ? expandedWidth : smallWidth
  const sourceUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autohide=1&autoplay=${
    autoplay ? '1' : '0'
  }`

  const webContent = (
    <Container
      height={isActive ? viewHeight + headerHeight : headerHeight}
      width={viewWidth}
    >
      <Header height={headerHeight}>
        <ActionButton
          onClick={sendSignalLeft}
          role="button"
        >
          {isExpanded ? shrinkArrow : growArrow}
        </ActionButton>
        <LiveIndicator
          active={!disableLive}
          centered={false}
        />
        <ActionButton
          onClick={sendSignalRight}
          role="button"
        >
          {!isActive ? caretUp : caretDown}
        </ActionButton>
      </Header>
      <VideoContainer
        height={isActive ? viewHeight : 0}
        width={viewWidth}
      >
        {isActive && (
          <Iframe
            allow={allowParams}
            allowFullScreen
            src={sourceUrl}
            title="YouTube Video Player"
          />
        )}
      </VideoContainer>
    </Container>
  )

  return (
    <Wrapper>
      <MobileContent
        allowParams={allowParams}
        videoId={videoId}
      />
      {webContent}
    </Wrapper>
  )
}

const Container = styled.div<{ height: number; width: number }>`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  padding: 0;
  background-color: ${colors.colorWhite};
  transition: height, width, 0.35s;
  overflow: hidden;
  z-index: 2;
`
const Header = styled.div<{ height: number }>`
  display: grid;
  grid: 1fr / 14px 1fr 14px;
  width: 100%;
  padding: 10px 18px;
  column-gap: 24px;
  height: ${({ height }) => height - 2}px;
  justify-content: stretch;
  align-items: center;
  border: solid 1px ${colors.colorGrey15};
  transition: width 0.35s;
  background-color: ${colors.colorWhite};
`
const ActionButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 22px;
  height: 22px;
  :hover {
    cursor: pointer;
  }
`
const VideoContainer = styled.div<{ width: number; height: number }>`
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  transition: height, width 0.35s;
`
const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
`

const Wrapper = styled.div`
  ${MobileContainer} {
    display: none;
  }
  @media (max-width: ${spaces.tabletBreakpoint}) {
    z-index: 2;
    ${Container} {
      display: none;
    }
    ${MobileContainer} {
      display: flex;
    }
  }
`

export default YoutubeLiveVideo
